import { useState } from 'react';
import { X } from 'lucide-react';

interface RoleModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (roleData: any) => void;
}

interface RoleFormData {
  permissions: {
    store: {
      mahalkumStore: boolean;
      baskittStore: boolean;
    };
    dashboard: {
      dashboard: boolean;
      financialDashboard: boolean;
    };
    pnl: {
      dashboard: boolean;
    };
    masterPermission: {
      roles: boolean;
      users: boolean;
    };
    order: {
      order: boolean;
      orderItems: boolean;
      cancelList: boolean;
    };
  };
}

export default function RoleModal({ isOpen, onClose, onSubmit }: RoleModalProps) {
  const [formData, setFormData] = useState<RoleFormData>({
    permissions: {
      store: {
        mahalkumStore: false,
        baskittStore: false,
      },
      dashboard: {
        dashboard: false,
        financialDashboard: false,
      },
      pnl: {
        dashboard: false,
      },
      masterPermission: {
        roles: false,
        users: false,
      },
      order: {
        order: false,
        orderItems: false,
        cancelList: false,
      },
    },
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handlePermissionChange = (category: string, permission: string, checked: boolean) => {
    setFormData(prev => ({
      ...prev,
      permissions: {
        ...prev.permissions,
        [category]: {
          ...prev.permissions[category as keyof typeof prev.permissions],
          [permission]: checked,
        },
      },
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      await onSubmit(formData);
      onClose();
      // Reset form
      setFormData({
        permissions: {
          store: {
            mahalkumStore: false,
            baskittStore: false,
          },
          dashboard: {
            dashboard: false,
            financialDashboard: false,
          },
          pnl: {
            dashboard: false,
          },
          masterPermission: {
            roles: false,
            users: false,
          },
          order: {
            order: false,
            orderItems: false,
            cancelList: false,
          },
        },
      });
    } catch (error) {
      console.error('Error creating role:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 z-40"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div 
        className={`fixed top-0 bg-white shadow-2xl transition-transform duration-300 ease-in-out z-50 ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
        style={{
          width: '980px',
          height: '100vh',
          right: '0px',
          top: '0px'
        }}
      >
        {/* Header */}
        <div className="bg-secondary-500 text-white p-6">
          <div className="flex justify-between items-start">
            <div>
                <h2 className="text-[40px] leading-none font-medium">Create Roles</h2>
                <p className="text-[24px] text-white/80 mt-2">Fill details to create roles</p>
            </div>
            <button 
              onClick={onClose}
              className="bg-[#426198] px-2 py-2 rounded-full text-white hover:text-gray-300 transition-colors"
            >
              <X size={24} />
            </button>
          </div>
        </div>

        {/* Form */}
        <div className="flex-1 overflow-y-auto" style={{ height: 'calc(100vh - 120px)' }}>
          <form onSubmit={handleSubmit} className="p-6">


            {/* Permissions Grid */}
            <div className="grid grid-cols-3 gap-5">
              {/* Store Section */}
              <div className="space-y-4">
                <h3 className="text-[18px] font-semibold text-[#111318] mb-3">Store</h3>
                <div className="space-y-3">
                  <label className="flex items-center p-3 border border-[#D6DDEE] rounded-lg bg-white hover:bg-gray-50 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.permissions.store.mahalkumStore}
                      onChange={(e) => handlePermissionChange('store', 'mahalkumStore', e.target.checked)}
                      className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                    <span className="ml-3 text-[14px] text-[#6A7698]">Mahalkum Store</span>
                  </label>
                  <label className="flex items-center p-3 border border-[#D6DDEE] rounded-lg bg-white hover:bg-gray-50 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.permissions.store.baskittStore}
                      onChange={(e) => handlePermissionChange('store', 'baskittStore', e.target.checked)}
                      className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                    <span className="ml-3 text-[14px] text-[#6A7698]">Baskitt Store</span>
                  </label>
                </div>
              </div>

              {/* Dashboard Section */}
              <div className="space-y-4">
                <h3 className="text-[18px] font-semibold text-[#111318] mb-3">Dashboard</h3>
                <div className="space-y-3">
                  <label className="flex items-center p-3 border border-[#D6DDEE] rounded-lg bg-white hover:bg-gray-50 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.permissions.dashboard.dashboard}
                      onChange={(e) => handlePermissionChange('dashboard', 'dashboard', e.target.checked)}
                      className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                    <span className="ml-3 text-[14px] text-[#6A7698]">Dashboard</span>
                  </label>
                  <label className="flex items-center p-3 border border-[#D6DDEE] rounded-lg bg-white hover:bg-gray-50 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.permissions.dashboard.financialDashboard}
                      onChange={(e) => handlePermissionChange('dashboard', 'financialDashboard', e.target.checked)}
                      className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                    <span className="ml-3 text-[14px] text-[#6A7698]">Financial Dashboard</span>
                  </label>
                </div>
              </div>

              {/* P&L Section */}
              <div className="space-y-4">
                <h3 className="text-[18px] font-semibold text-[#111318] mb-3">P&L</h3>
                <div className="space-y-3">
                  <label className="flex items-center p-3 border border-[#D6DDEE] rounded-lg bg-white hover:bg-gray-50 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.permissions.pnl.dashboard}
                      onChange={(e) => handlePermissionChange('pnl', 'dashboard', e.target.checked)}
                      className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                    <span className="ml-3 text-[14px] text-[#6A7698]">Dashboard</span>
                  </label>
                </div>
              </div>

              {/* Master Permission Section */}
              <div className="space-y-4">
                <h3 className="text-[18px] font-semibold text-[#111318] mb-3">Master Permission</h3>
                <div className="space-y-3">
                  <label className="flex items-center p-3 border border-[#D6DDEE] rounded-lg bg-white hover:bg-gray-50 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.permissions.masterPermission.roles}
                      onChange={(e) => handlePermissionChange('masterPermission', 'roles', e.target.checked)}
                      className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                    <span className="ml-3 text-[14px] text-[#6A7698]">Roles</span>
                  </label>
                  <label className="flex items-center p-3 border border-[#D6DDEE] rounded-lg bg-white hover:bg-gray-50 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.permissions.masterPermission.users}
                      onChange={(e) => handlePermissionChange('masterPermission', 'users', e.target.checked)}
                      className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                    <span className="ml-3 text-[14px] text-[#6A7698]">Users</span>
                  </label>
                </div>
              </div>

              {/* Order Section */}
              <div className="space-y-4">
                <h3 className="text-[18px] font-semibold text-[#111318] mb-3">Order</h3>
                <div className="space-y-3">
                  <label className="flex items-center p-3 border border-[#D6DDEE] rounded-lg bg-white hover:bg-gray-50 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.permissions.order.order}
                      onChange={(e) => handlePermissionChange('order', 'order', e.target.checked)}
                      className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                    <span className="ml-3 text-[14px] text-[#6A7698]">Order</span>
                  </label>
                  <label className="flex items-center p-3 border border-[#D6DDEE] rounded-lg bg-white hover:bg-gray-50 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.permissions.order.orderItems}
                      onChange={(e) => handlePermissionChange('order', 'orderItems', e.target.checked)}
                      className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                    <span className="ml-3 text-[14px] text-[#6A7698]">Order Items</span>
                  </label>
                  <label className="flex items-center p-3 border border-[#D6DDEE] rounded-lg bg-white hover:bg-gray-50 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.permissions.order.cancelList}
                      onChange={(e) => handlePermissionChange('order', 'cancelList', e.target.checked)}
                      className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                    <span className="ml-3 text-[14px] text-[#6A7698]">Cancel List</span>
                  </label>
                </div>
              </div>
            </div>

            {/* Submit Button */}
            <div className="mt-8">
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full rounded-lg bg-[#F6C600] py-3 px-6 text-[18px] font-medium text-black transition-colors hover:bg-[#e1b500] disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? 'Creating...' : 'Submit'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
}
